import React, { useState, useEffect } from 'react';
import { getDatabase, ref as dbRef, get } from 'firebase/database';
import './AnalysisPage.css';
import { useUsersData } from "../../Helpers/UsersContext";
import { BuildingType } from "../../types/BuildingTypeEnums";
import { BuildingSummary } from "../../types/protocols";
import { translate } from "../../types/EnumTranslations";
import ServicesSummarySection from './ServicesSummarySection';
import BuildingLogsModal from './components/BuildingLogsModal';
import {
    BuildingTypeOption,
    DateRange,
    ServiceData,
    ServiceLogEntry,
    ServiceType,
    TimeGrouping, UserGroup
} from "./types/analysis.types";
import {getDefaultDateRange} from "./utils/dateUtils";
import {
    extractServiceLogEntries,
    getCollectedValuesSummary,
    getServicesSummary,
    transformDataForChart
} from "./utils/dataProcessing";
import AnalysisControls from "./components/AnalysisControls";
import AnalysisChart from "./components/AnalysisChart";
import {SERVICE_COLORS} from "./utils/constants";
import DataTablesSection from "./components/DataTablesSection";
import COLORS from "../../Helpers/utils";
import { Helmet } from 'react-helmet-async';

// Define sort options type
type SortOption = 'visits' | 'sacks' | 'tanks' | 'cleaning' | 'solarControl' | 'deposit' | 'wasteBarrels' | 'compost';

const AnalysisPage: React.FC = () => {
    // State management
    const [serviceData, setServiceData] = useState<ServiceData[]>([]);
    const [logEntries, setLogEntries] = useState<ServiceLogEntry[]>([]);
    const [commentEntries, setCommentEntries] = useState<ServiceLogEntry[]>([]);
    const [selectedBuildingTypes, setSelectedBuildingTypes] = useState<BuildingType[]>([]);
    const [buildingTypeOptions] = useState<BuildingTypeOption[]>([
        { value: BuildingType.toiletArchipelago, label: translate(BuildingType.toiletArchipelago) },
        { value: BuildingType.toiletReserve, label: translate(BuildingType.toiletReserve) }
    ]);
    const [selectedBuildingTypeOptions, setSelectedBuildingTypeOptions] = useState<BuildingTypeOption[]>([]);
    const [selectedUserGroup, setSelectedUserGroup] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [serviceTypes, setServiceTypes] = useState<ServiceType[]>([]);
    const [dateRange, setDateRange] = useState<DateRange>(getDefaultDateRange());
    const [serviceTitles, setServiceTitles] = useState<Record<string, string>>({});
    const [timeGrouping, setTimeGrouping] = useState<TimeGrouping>('weekly');
    const [buildings, setBuildings] = useState<BuildingSummary[]>([]);
    const [servicesConfigs, setServicesConfigs] = useState<Record<string, any>>({});
    const [individualVisits, setIndividualVisits] = useState<ServiceLogEntry[]>([]);
    const { users } = useUsersData();
    const [userGroups, setUserGroups] = useState<UserGroup[]>([]);

    // New state for combined toplist
    const [sortBy, setSortBy] = useState<SortOption>('visits');
    const [isToplistCollapsed, setIsToplistCollapsed] = useState<boolean>(true);

    // Modal state
    const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
    const [selectedBuilding, setSelectedBuilding] = useState<BuildingSummary | null>(null);

    // Combined toplist computation
    const getCombinedTopList = () => {
        const buildingStats: Record<string, {
            visits: number;
            sacks: number;
            tanks: number;
            cleaning: number;
            solarControl: number;
            deposit: number;
            wasteBarrels: number;
            compost: number;
            name: string
        }> = {};

        // Initialize all buildings
        buildings.forEach(building => {
            buildingStats[building.key] = {
                visits: 0,
                sacks: 0,
                tanks: 0,
                cleaning: 0,
                solarControl: 0,
                deposit: 0,
                wasteBarrels: 0,
                compost: 0,
                name: building.name || building.key
            };
        });

        // Count visits
        individualVisits.forEach(entry => {
            if (buildingStats[entry.buildingKey]) {
                buildingStats[entry.buildingKey].visits++;
            }
        });

        // Count all service types
        logEntries.forEach(entry => {
            if (buildingStats[entry.buildingKey]) {
                // Find the service type configuration to check if it collects values
                const serviceType = serviceTypes.find(st => st.key === entry.serviceType);
                const collectValue = serviceType?.collectValue ?? false;

                switch (entry.serviceType) {
                    case 'collectGarbage':
                        buildingStats[entry.buildingKey].sacks += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'toiletPumpout':
                        buildingStats[entry.buildingKey].tanks += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'generalCleaning':
                        buildingStats[entry.buildingKey].cleaning += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'solarCheckTankAndFan':
                        buildingStats[entry.buildingKey].solarControl += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'collectCansSeparately':
                        buildingStats[entry.buildingKey].deposit += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'handleWasteBarrels':
                        buildingStats[entry.buildingKey].wasteBarrels += collectValue ? (entry.value ?? 0) : 1;
                        break;
                    case 'handleCompost':
                        buildingStats[entry.buildingKey].compost += collectValue ? (entry.value ?? 0) : 1;
                        break;
                }
            }
        });

        // Convert to array and filter out buildings with no data
        const topList = Object.entries(buildingStats)
            .map(([key, stats]) => ({ key, ...stats }))
            .filter(item => item.visits > 0 || item.sacks > 0 || item.tanks > 0 || item.cleaning > 0 || item.solarControl > 0 || item.deposit > 0 || item.wasteBarrels > 0 || item.compost > 0);

        // Sort based on selected criteria
        switch (sortBy) {
            case 'visits':
                return topList.sort((a, b) => b.visits - a.visits);
            case 'sacks':
                return topList.sort((a, b) => b.sacks - a.sacks);
            case 'tanks':
                return topList.sort((a, b) => b.tanks - a.tanks);
            case 'cleaning':
                return topList.sort((a, b) => b.cleaning - a.cleaning);
            case 'solarControl':
                return topList.sort((a, b) => b.solarControl - a.solarControl);
            case 'deposit':
                return topList.sort((a, b) => b.deposit - a.deposit);
            case 'wasteBarrels':
                return topList.sort((a, b) => b.wasteBarrels - a.wasteBarrels);
            case 'compost':
                return topList.sort((a, b) => b.compost - a.compost);
            default:
                return topList;
        }
    };

    const combinedTopList = getCombinedTopList();

    const toggleToplist = () => setIsToplistCollapsed(!isToplistCollapsed);

    // Modal functions
    const handleBuildingClick = (buildingKey: string) => {
        const building = buildings.find(b => b.key === buildingKey);
        if (building) {
            window.open(`building/${buildingKey}`)
        }
    };

    const handleBuildingLogClick = (buildingKey: string) => {
        const building = buildings.find(b => b.key === buildingKey);
        if (building) {
            setSelectedBuilding(building);
            setIsModalOpen(true);
        }
    };

    const handleCloseModal = () => {
        setIsModalOpen(false);
        setSelectedBuilding(null);
    };

    // Main data fetching effect
    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true);
                const db = getDatabase();

                // Fetch buildings summary
                const buildingsSummaryRef = dbRef(db, 'buildingsSummary');
                const buildingsSummarySnapshot = await get(buildingsSummaryRef);

                let buildingsData: BuildingSummary[] = [];
                if (buildingsSummarySnapshot.exists()) {
                    const summaryData = buildingsSummarySnapshot.val();
                    buildingsData = Object.entries(summaryData).map(([key, value]: [string, any]) => ({
                        key,
                        ...value
                    }));
                    setBuildings(buildingsData);
                }

                // Set default building types if none selected
                if (selectedBuildingTypes.length === 0) {
                    const defaultBuildingTypes = [BuildingType.toiletArchipelago, BuildingType.toiletReserve];
                    setSelectedBuildingTypes(defaultBuildingTypes);
                    const defaultOptions = buildingTypeOptions.filter(option =>
                        defaultBuildingTypes.includes(option.value)
                    );
                    setSelectedBuildingTypeOptions(defaultOptions);
                }

                // Fetch services data
                const servicesRef = dbRef(db, 'buildingServices');
                const snapshot = await get(servicesRef);

                if (!snapshot.exists()) {
                    console.error('No data available in the specified path');
                    setIsLoading(false);
                    return;
                }

                const data = snapshot.val();
                const servicesConfigsData = data.servicesConfig || {};
                setServicesConfigs(servicesConfigsData);

                // Process service types
                const typesObj = data.servicesTypes || {};
                const filteredTypesObj = Object.fromEntries(
                    Object.entries(typesObj).filter(([key, value]) => key !== 'generalComment')
                );
                const typesArray: ServiceType[] = Object.entries(filteredTypesObj).map(([key, value]: [string, any]) => ({
                    ...value,
                    key: key
                }));
                setServiceTypes(typesArray);

                // Set service titles
                const titles: Record<string, string> = {};
                Object.entries(typesObj).forEach(([key, value]: [string, any]) => {
                    titles[key] = value.title || key;
                });
                setServiceTitles(titles);

                // Extract and process log entries
                const entries = extractServiceLogEntries(
                    data,
                    dateRange,
                    buildingsData,
                    selectedBuildingTypes,
                    selectedUserGroup,
                    false
                );
                setLogEntries(entries);

                // Extract and process comment entries
                const comments = extractServiceLogEntries(
                    data,
                    dateRange,
                    buildingsData,
                    selectedBuildingTypes,
                    selectedUserGroup,
                    true
                );
                setCommentEntries(comments);

                // Extract individual visits
                const visits = extractIndividualVisits(entries);
                setIndividualVisits(visits);

                // Transform data for chart
                const transformedData = transformDataForChart(
                    entries,
                    timeGrouping,
                    buildingsData,
                    dateRange
                );
                setServiceData(transformedData);

                // Fetch user groups
                const groupsSnapshot = await get(dbRef(db, 'users/groups'));
                if (groupsSnapshot.exists()) {
                    const groupsData = groupsSnapshot.val();
                    const groups: UserGroup[] = Object.entries(groupsData).map(([key, value]: [string, any]) => ({
                        key,
                        name: value.name || key
                    }));
                    setUserGroups(groups);
                }

                setIsLoading(false);
            } catch (error) {
                console.error('Error fetching data:', error);
                setIsLoading(false);
            }
        };

        fetchData();
    }, [selectedBuildingTypes, dateRange.start, dateRange.end, timeGrouping, selectedUserGroup]);

    // Helper functions for summary data
    const getSelectedGroupName = (): string => {
        if (!selectedUserGroup) return "alla företag";
        const group = userGroups.find(g => g.key === selectedUserGroup);
        return group ? group.name : selectedUserGroup;
    };

    const getCompanyName = (companyKey: string): string => {
        if (companyKey === 'unknown') return 'Okänd';
        const group = userGroups.find(g => g.key === companyKey);
        return group ? group.name : companyKey;
    };

    const getTotalVisitsCount = (): number => {
        return individualVisits.length;
    };

    const getVisitsByCompany = (): Record<string, number> => {
        const visitsByCompany: Record<string, number> = {};

        individualVisits.forEach(entry => {
            const building = buildings.find(b => b.key === entry.buildingKey);
            let companyKey = 'unknown';
            if (building && building.responsibleGroups && building.responsibleGroups.length > 0) {
                companyKey = building.responsibleGroups[0];
            }

            visitsByCompany[companyKey] = (visitsByCompany[companyKey] || 0) + 1;
        });

        return visitsByCompany;
    };

    // Extract individual visits helper
    const extractIndividualVisits = (entries: ServiceLogEntry[]): ServiceLogEntry[] => {
        const visitsByDateAndBuilding: Record<string, Record<string, ServiceLogEntry>> = {};

        entries.forEach((entry) => {
            const dateKey = entry.date;
            if (!visitsByDateAndBuilding[dateKey]) {
                visitsByDateAndBuilding[dateKey] = {};
            }
            const currentEntry = visitsByDateAndBuilding[dateKey][entry.buildingKey];
            if (!currentEntry || new Date(entry.dateString) > new Date(currentEntry.dateString)) {
                visitsByDateAndBuilding[dateKey][entry.buildingKey] = entry;
            }
        });

        const visits: ServiceLogEntry[] = [];
        Object.values(visitsByDateAndBuilding).forEach(buildingEntries => {
            Object.values(buildingEntries).forEach(entry => {
                visits.push(entry);
            });
        });

        return visits.sort((a, b) => new Date(b.dateString).getTime() - new Date(a.dateString).getTime());
    };

    // Prepare summary data
    const buildingTypeSummaryData = getServicesSummary(
        logEntries,
        serviceTypes,
        serviceTitles,
        buildings,
        buildingTypeOptions,
        'buildingType'
    );

    const companySummaryData = getServicesSummary(
        logEntries,
        serviceTypes,
        serviceTitles,
        buildings,
        userGroups,
        'company'
    );

    const collectedValuesSummary = getCollectedValuesSummary(logEntries, serviceTypes);

    const isServiceEnabledForBuilding = (buildingKey: string, serviceType: string): boolean => {
        const buildingConfig = servicesConfigs[buildingKey];
        if (!buildingConfig) return false;

        const serviceConfig = buildingConfig[serviceType];
        if (!serviceConfig) return false;

        return serviceConfig.enabled || false;
    };

    const getServiceDisplayValue = (buildingKey: string, serviceType: string, value: number): string => {
        if (!isServiceEnabledForBuilding(buildingKey, serviceType)) {
            return '-';
        }
        return value.toString();
    };

    return (
        <div className="analysis-container">
            <Helmet>
                <title>Analys - Molnfastigheter</title>
            </Helmet>
            <h1 className="analysis-title">Besöksanalys per företag</h1>

            {/* Main Analysis Card with Controls and Chart */}
            <div className="analysis-card">
                <AnalysisControls
                    buildingTypeOptions={buildingTypeOptions}
                    selectedBuildingTypeOptions={selectedBuildingTypeOptions}
                    onBuildingTypeChange={setSelectedBuildingTypeOptions}
                    selectedBuildingTypes={selectedBuildingTypes}
                    onSelectedBuildingTypesChange={setSelectedBuildingTypes}
                    selectedUserGroup={selectedUserGroup}
                    onUserGroupChange={setSelectedUserGroup}
                    userGroups={userGroups}
                    timeGrouping={timeGrouping}
                    onTimeGroupingChange={setTimeGrouping}
                    dateRange={dateRange}
                    onDateRangeChange={setDateRange}
                />

                <AnalysisChart
                    isLoading={isLoading}
                    serviceData={serviceData}
                    timeGrouping={timeGrouping}
                    getCompanyName={getCompanyName}
                    getSelectedGroupName={getSelectedGroupName}
                    selectedUserGroup={selectedUserGroup}
                />
            </div>

            <br/>

            {/* Building Type Summary Section */}
            {serviceTypes.length > 0 && (
                <ServicesSummarySection
                    title="Sammanfattning - Utförda tjänster per byggnadstyp"
                    data={buildingTypeSummaryData.data}
                    columns={buildingTypeSummaryData.columns}
                    pieChartData={buildingTypeSummaryData.pieData}
                    pieChartTitle="Fördelning per byggnadstyp"
                    colors={SERVICE_COLORS}
                    totalEntries={logEntries.length}
                    selectedUserGroup={selectedUserGroup}
                    getSelectedGroupName={getSelectedGroupName}
                    showColumnsWithZeroEntries={true}
                />
            )}

            {/* Company Summary Section */}
            {serviceTypes.length > 0 && (
                <ServicesSummarySection
                    title="Sammanfattning - Utförda tjänster per företag"
                    data={companySummaryData.data}
                    columns={companySummaryData.columns}
                    pieChartData={companySummaryData.pieData}
                    pieChartTitle="Fördelning per företag"
                    colors={SERVICE_COLORS}
                    totalEntries={logEntries.length}
                    selectedUserGroup={selectedUserGroup}
                    getSelectedGroupName={getSelectedGroupName}
                    showColumnsWithZeroEntries={false}
                />
            )}

            <br/>

            <h2 style={{marginTop: '20px'}}>Sammanställning</h2>

            {/* Main Summary Card */}
            <div className="analysis-card">
                <h2 className="summary-title">Byggnadsbesök</h2>
                <p>Totalt {getTotalVisitsCount()} byggnadsbesök </p>
            </div>

            <div className="analysis-card">
                <h2 className="summary-title">Besök per företag</h2>
                {Object.entries(getVisitsByCompany()).map(([companyKey, visits]) => (
                    <p key={companyKey} style={{ margin: '2px 0' }}>
                        {getCompanyName(companyKey)}: {visits} besök
                    </p>
                ))}
            </div>

            <div className="analysis-card">
                <h2 className="summary-title">Totala insamlade mängder</h2>
                {collectedValuesSummary.map((summary) => (
                    summary && <p key={summary.serviceType} style={{ margin: '2px 0' }}>
                        {summary.serviceName}: {summary.totalValue.toLocaleString('sv-SE')} {summary.unit}
                        {summary.entryCount > 0 && (
                            <span style={{ color: '#666', fontSize: '0.9em' }}>
                                {' '}({summary.entryCount} registreringar)
                            </span>
                        )}
                    </p>
                ))}
            </div>

            <br/>

            {/* Combined Toplist */}
            <div className="analysis-card">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginBottom: '15px' }}>
                    <h2 className="summary-title" style={{ margin: 0 }}>Topplista byggnader</h2>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <label htmlFor="sort-select" style={{ fontSize: '14px', color: '#666' }}>
                            Sortera på:
                        </label>
                        <select
                            id="sort-select"
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value as SortOption)}
                            style={{
                                padding: '4px 8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '14px'
                            }}
                        >
                            <option value="visits">Besök</option>
                            <option value="compost">Kompost</option>
                            <option value="wasteBarrels">Latrintunnor</option>
                            <option value="deposit">Pant</option>
                            <option value="solarControl">Solcellskontroll</option>
                            <option value="sacks">Sopsäckar</option>
                            <option value="cleaning">Städning</option>
                            <option value="tanks">Tankar</option>
                        </select>
                    </div>
                </div>

                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                    <tr style={{ borderBottom: '2px solid #eee' }}>
                        <th style={{ textAlign: 'left', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Byggnad</th>
                        <th style={{ textAlign: 'left', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Logg</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Besök</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Sopsäckar</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Tankar</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Städning</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Solcellskontroll</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Pant</th>
                        <th style={{ textAlign: 'right', padding: '8px', borderRight: '1px solid #f0f0f0' }}>Latrintunnor</th>
                        <th style={{ textAlign: 'right', padding: '8px' }}>Kompost</th>
                    </tr>
                    </thead>
                    <tbody>
                    {combinedTopList.slice(0, 10).map((item, index) => (
                        <tr key={item.key} style={{
                            borderBottom: '1px solid #f5f5f5',
                            backgroundColor: index % 2 === 1 ? '#f9f9f9' : 'transparent'
                        }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    {item.name}
                                </button>
                            </td>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingLogClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    Detaljer
                                </button>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{item.visits}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectGarbage', item.sacks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'toiletPumpout', item.tanks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'generalCleaning', item.cleaning)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'solarCheckTankAndFan', item.solarControl)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectCansSeparately', item.deposit)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'handleWasteBarrels', item.wasteBarrels)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{getServiceDisplayValue(item.key, 'handleCompost', item.compost)}</td>
                        </tr>
                    ))}
                    {combinedTopList.length > 10 && isToplistCollapsed && (
                        <tr>
                            <td
                                colSpan={10}
                                onClick={toggleToplist}
                                style={{
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    color: COLORS.primaryDark,
                                    padding: '12px',
                                    borderTop: '2px solid #f0f0f0',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                Visa alla {combinedTopList.length} byggnader
                            </td>
                        </tr>
                    )}
                    {!isToplistCollapsed && combinedTopList.slice(10).map((item, index) => (
                        <tr key={item.key} style={{
                            borderBottom: '1px solid #f5f5f5',
                            backgroundColor: (index + 10) % 2 === 1 ? '#f9f9f9' : 'transparent'
                        }}>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    {item.name}
                                </button>
                            </td>
                            <td style={{ padding: '8px', borderRight: '1px solid #f0f0f0' }}>
                                <button
                                    onClick={() => handleBuildingLogClick(item.key)}
                                    style={{
                                        background: 'none',
                                        border: 'none',
                                        color: COLORS.primaryDark,
                                        textDecoration: 'underline',
                                        cursor: 'pointer',
                                        padding: 0,
                                        font: 'inherit'
                                    }}
                                    onMouseEnter={(e) => e.currentTarget.style.color = COLORS.primary}
                                    onMouseLeave={(e) => e.currentTarget.style.color = COLORS.primaryDark}
                                >
                                    Detaljer
                                </button>
                            </td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{item.visits}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectGarbage', item.sacks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'toiletPumpout', item.tanks)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'generalCleaning', item.cleaning)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'solarCheckTankAndFan', item.solarControl)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'collectCansSeparately', item.deposit)}</td>
                            <td style={{ padding: '8px', textAlign: 'right', borderRight: '1px solid #f0f0f0' }}>{getServiceDisplayValue(item.key, 'handleWasteBarrels', item.wasteBarrels)}</td>
                            <td style={{ padding: '8px', textAlign: 'right' }}>{getServiceDisplayValue(item.key, 'handleCompost', item.compost)}</td>
                        </tr>
                    ))}
                    {!isToplistCollapsed && (
                        <tr>
                            <td
                                colSpan={10}
                                onClick={toggleToplist}
                                style={{
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    color: COLORS.primaryDark,
                                    padding: '12px',
                                    borderTop: '2px solid #f0f0f0',
                                    backgroundColor: '#f9f9f9'
                                }}
                            >
                                Dölj
                            </td>
                        </tr>
                    )}
                    </tbody>
                </table>
            </div>

            {/* Data Tables Section */}
            <DataTablesSection
                serviceTypes={serviceTypes}
                individualVisits={individualVisits}
                logEntries={logEntries}
                commentEntries={commentEntries}
                buildings={buildings}
                users={users || []}
                serviceTitles={serviceTitles}
                selectedUserGroup={selectedUserGroup}
                getSelectedGroupName={getSelectedGroupName}
            />

            {/* Building Logs Modal */}
            <BuildingLogsModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                building={selectedBuilding}
                logEntries={logEntries}
                commentEntries={commentEntries}
                individualVisits={individualVisits}
                users={users || []}
                serviceTypes={serviceTypes}
                serviceTitles={serviceTitles}
            />
        </div>
    );
};

export default AnalysisPage;